import { useEffect, useRef } from 'react';
import { ChatCitations } from './ChatCitations';
import { MarkdownContent } from './MarkdownContent';
import type { ChatMessage } from '../types';
import styles from './ChatMessageList.module.css';

export type ChatMessageListProps = {
  messages: ChatMessage[];
  loading: boolean;
};

function visibleMessages(messages: ChatMessage[]): ChatMessage[] {
  return messages.filter((message) => message.role === 'user' || message.role === 'assistant');
}

export function ChatMessageList({ messages, loading }: ChatMessageListProps) {
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const visible = visibleMessages(messages);

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [visible]);

  if (loading) {
    return (
      <p className={`${styles.loading} muted`} aria-live="polite">
        Loading messages…
      </p>
    );
  }

  if (visible.length === 0) {
    return (
      <p className={`${styles.empty} muted`}>
        No messages in this chat yet. Pick a suggested question below or type your own.
      </p>
    );
  }

  return (
    <div className={styles.list} role="log" aria-live="polite" aria-relevant="additions">
      {visible.map((message) => {
        const isUser = message.role === 'user';

        return (
          <div
            key={message.id}
            className={`${styles.messageRow} ${isUser ? styles.messageRowUser : styles.messageRowAssistant}`}
          >
            <article
              className={`${styles.bubble} ${isUser ? styles.bubbleUser : styles.bubbleAssistant}`}
              aria-label={isUser ? 'Your message' : 'Assistant message'}
            >
              {isUser ? (
                <p className={styles.userText}>{message.content}</p>
              ) : (
                <>
                  <MarkdownContent content={message.content} />
                  {message.citations?.length ? <ChatCitations citations={message.citations} /> : null}
                </>
              )}
            </article>
          </div>
        );
      })}
      <div ref={scrollAnchorRef} className={styles.scrollAnchor} aria-hidden />
    </div>
  );
}
